import React, { useEffect, useMemo, useState } from 'react';
import { FaProjectDiagram, FaSearch, FaCalendarAlt, FaCheckCircle, FaClock, FaListOl, FaArrowUp, FaArrowDown, FaFilePdf } from 'react-icons/fa';
import './ReportesProyectos.css';
import { fetchAuth } from '../../utils/api';

const toDate = (s) => {
  const a = String(s || '');
  const part = a.split('T')[0].split(' ')[0];
  if (!part) return null;
  return new Date(part + 'T00:00:00');
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return Math.floor(ms / 86400000);
};

function ReportesProyectos() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [detalles, setDetalles] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('TODOS'); // TODOS | PENDIENTE | EN_PROGRESO | COMPLETADO
  const [busqueda, setBusqueda] = useState('');
  const [periodKey, setPeriodKey] = useState('MES');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedJefe, setSelectedJefe] = useState('TODOS');
  const [sortKey, setSortKey] = useState('nombre');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getCurrentRange = () => {
    const now = new Date();
    if (periodKey === '30D') {
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const start = new Date(end);
      start.setDate(start.getDate() - 29);
      return [start, end];
    }
    if (periodKey === 'MES') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return [start, end];
    }
    if (periodKey === 'ANIO') {
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      return [start, end];
    }
    if (periodKey === 'RANGO' && customStart && customEnd) {
      const start = toDate(customStart);
      const end = toDate(customEnd);
      if (start && end) return [start, end];
    }
    return null;
  };

  const getPreviousRange = () => {
    const now = new Date();
    if (periodKey === '30D') {
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 59);
      return [start, end];
    }
    if (periodKey === 'MES') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return [start, end];
    }
    if (periodKey === 'ANIO') {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return [start, end];
    }
    if (periodKey === 'RANGO' && customStart && customEnd) {
      const cs = toDate(customStart);
      const ce = toDate(customEnd);
      if (cs && ce) {
        const len = Math.max(1, daysBetween(cs, ce) + 1);
        const end = new Date(cs);
        end.setDate(end.getDate() - 1);
        const start = new Date(end);
        start.setDate(start.getDate() - (len - 1));
        return [start, end];
      }
    }
    return null;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAuth('/api/admin/proyectos/');
        const list = data.proyectos || [];
        setProyectos(list);
        // Cargar detalles por proyecto en paralelo (mejor esfuerzo)
        const ids = list.map(p => p.id_proyecto);
        const fetches = ids.map(async (id) => {
          // Primero intentar como Jefe; si no tiene permisos, caer a vista Colaborador
          try {
            const d = await fetchAuth(`/api/jefe/proyectos/${id}/detalle/`);
            return { id, detalle: d };
          } catch (e) {
            try {
              const d2 = await fetchAuth(`/api/colaborador/proyectos/${id}/detalle/`);
              return { id, detalle: d2 };
            } catch (e2) {
              return { id, detalle: null };
            }
          }
        });
        const results = await Promise.all(fetches);
        const detMap = {};
        results.forEach(r => { detMap[r.id] = r.detalle; });
        setDetalles(detMap);
      } catch (e) {
        setError(e.message || 'Error al cargar proyectos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const proyectosFiltrados = useMemo(() => {
    const s = String(busqueda || '').toLowerCase();
    const norm = (v) => String(v || '').toLowerCase();
    const toKeyEstado = (estado) => {
      const e = String(estado || '').toUpperCase();
      if (e.includes('PEND')) return 'PENDIENTE';
      if (e.includes('PROGRES')) return 'EN_PROGRESO';
      if (e.includes('COMPLET')) return 'COMPLETADO';
      return e || 'PENDIENTE';
    };
    const rango = getCurrentRange();
    const inRange = (p) => {
      if (!rango) return true;
      const d = detalles[p.id_proyecto] || null;
      const start = toDate(p.fecha_inicio);
      const endReal = toDate(d?.fecha_ultima_tarea_completada || d?.fecha_fin_real);
      const endPlan = toDate(p.fecha_fin);
      const endEff = endReal || endPlan || new Date(2099, 11, 31);
      const [rs, re] = rango;
      return (start <= re && endEff >= rs);
    };
    const arr = (proyectos || []).filter(p => {
      const matchText = norm(p.nombre_proyecto).includes(s) || norm(p.jefe_nombre).includes(s);
      const key = toKeyEstado(p.estado);
      const matchEstado = filtroEstado === 'TODOS' || key === filtroEstado;
      const matchJefe = selectedJefe === 'TODOS' || String(p.jefe_nombre || '') === String(selectedJefe);
      return matchText && matchEstado && matchJefe && inRange(p);
    });
    const sorted = arr.slice().sort((a,b) => {
      const ka = sortKey;
      const va = ka==='nombre' ? String(a.nombre_proyecto||'').toLowerCase() : ka==='estado' ? String(a.estado||'').toLowerCase() : ka==='jefe' ? String(a.jefe_nombre||'').toLowerCase() : a.id_proyecto;
      const vb = ka==='nombre' ? String(b.nombre_proyecto||'').toLowerCase() : ka==='estado' ? String(b.estado||'').toLowerCase() : ka==='jefe' ? String(b.jefe_nombre||'').toLowerCase() : b.id_proyecto;
      if (typeof va === 'string' && typeof vb === 'string') return sortDir==='asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir==='asc' ? (va - vb) : (vb - va);
    });
    return sorted;
  }, [proyectos, busqueda, filtroEstado, selectedJefe, sortKey, sortDir, detalles, periodKey, customStart, customEnd]);

  const resumen = useMemo(() => {
    const r = { total: proyectos.length, pendientes: 0, progreso: 0, completados: 0 };
    proyectos.forEach(p => {
      const e = String(p.estado || '').toUpperCase();
      if (e.includes('PEND')) r.pendientes += 1;
      else if (e.includes('PROGRES')) r.progreso += 1;
      else if (e.includes('COMPLET')) r.completados += 1;
    });
    return r;
  }, [proyectos]);

  const fmt = (s) => {
    const a = String(s || '');
    const part = a.split('T')[0].split(' ')[0];
    if (!part) return '-';
    const [y, m, d] = part.split('-');
    return `${d}/${m}/${y}`;
  };

  const kpis = useMemo(() => {
    const tot = proyectos.length;
    let started = 0;
    let tasksSum = 0;
    let completed = 0;
    let completedWithReal = 0;
    let onTime = 0;
    let delaySum = 0;
    proyectos.forEach(p => {
      const d = detalles[p.id_proyecto] || null;
      const e = String(p.estado || '').toUpperCase();
      if (d?.fecha_inicio_real) started += 1;
      const tt = d?.metricas?.tareas_totales ?? (d?.etapas ? d.etapas.reduce((acc, e) => acc + (e.items?.length || 0), 0) : 0);
      tasksSum += Number(tt || 0);
      if (e.includes('COMPLET')) {
        completed += 1;
        const finPlan = toDate(p.fecha_fin);
        const finReal = toDate(d?.fecha_ultima_tarea_completada || d?.fecha_fin_real);
        if (finPlan && finReal) {
          completedWithReal += 1;
          if (finReal <= finPlan) onTime += 1;
          const diff = daysBetween(finPlan, finReal);
          if (typeof diff === 'number') delaySum += Math.max(0, diff);
        }
      }
    });
    const startRate = tot ? Math.round((started / tot) * 100) : 0;
    const avgTasks = tot ? Math.round(tasksSum / tot) : 0;
    const onTimeRate = completedWithReal ? Math.round((onTime / completedWithReal) * 100) : 0;
    const avgDelay = completedWithReal ? Math.round(delaySum / completedWithReal) : 0;
    const curRange = getCurrentRange();
    const prevRange = getPreviousRange();
    const calcForRange = (range) => {
      if (!range) return { active: 0, done: 0 };
      const [rs, re] = range;
      let active = 0;
      let done = 0;
      proyectos.forEach(p => {
        const d = detalles[p.id_proyecto] || null;
        const s = toDate(p.fecha_inicio);
        const fr = toDate(d?.fecha_ultima_tarea_completada || d?.fecha_fin_real);
        const fp = toDate(p.fecha_fin);
        const endEff = fr || fp || new Date(2099, 11, 31);
        if (s && s <= re && endEff >= rs && p.estado !== 'COMPLETADO') active += 1;
        if (p.estado === 'COMPLETADO' || (fr && fr >= rs && fr <= re)) done += 1;
      });
      return { active, done };
    };
    const cur = calcForRange(curRange);
    const prev = calcForRange(prevRange);
    const diffActive = cur.active - prev.active;
    const diffDone = cur.done - prev.done;
    const plannedInRange = (range) => {
      if (!range) return 0;
      const [rs, re] = range;
      let c = 0;
      proyectos.forEach(p => {
        const fp = toDate(p.fecha_fin);
        if (fp && fp >= rs && fp <= re) c += 1;
      });
      return c;
    };
    const plannedCur = plannedInRange(curRange);
    const plannedPrev = plannedInRange(prevRange);
    const completionRateCur = plannedCur ? Math.round((cur.done / plannedCur) * 100) : 0;
    const completionRatePrev = plannedPrev ? Math.round((prev.done / plannedPrev) * 100) : 0;
    const diffCompletionRate = completionRateCur - completionRatePrev;
    return { tot, startRate, avgTasks, onTimeRate, avgDelay, completed, cur, prev, diffActive, diffDone, plannedCur, completionRateCur, completionRatePrev, diffCompletionRate };
  }, [proyectos, detalles]);


  const jefeOptions = useMemo(() => {
    const set = new Set();
    proyectos.forEach(p => { if (p.jefe_nombre) set.add(p.jefe_nombre); });
    return Array.from(set);
  }, [proyectos]);

  const pageCount = Math.max(1, Math.ceil(proyectosFiltrados.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return proyectosFiltrados.slice(start, start + pageSize);
  }, [proyectosFiltrados, page, pageSize]);

  const exportPDF = () => {
    const run = async () => {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text('Informe de Proyectos', 40, 40);
      doc.setFontSize(11);
      const rango = getCurrentRange();
      if (rango) {
        const [rs, re] = rango;
        doc.text(`Período: ${rs.toLocaleDateString()} — ${re.toLocaleDateString()}`, 40, 60);
      }
      const head1 = [[
        'ID','Proyecto','Jefe','Estado','T','Pend','Canc','Compl','Rev','Colabs'
      ]];
      const body1 = [];
      const head2 = [[
        'Avance','Ini P','Ini R','Fin P','Fin R','Desv(d)'
      ]];
      const body2 = [];
      pageData.forEach(p => {
        const d = detalles[p.id_proyecto] || null;
        const tareasTotales = d?.metricas?.tareas_totales ?? (d?.etapas ? d.etapas.reduce((acc,e)=>acc + (e.items?.length||0),0) : 0);
        const pend = d?.metricas?.tareas_pendientes ?? null;
        const rech = d?.metricas?.tareas_canceladas ?? null;
        const aprob = d?.metricas?.tareas_completadas ?? null;
        const enProg = d?.metricas?.tareas_en_revision ?? null;
        const colCount = (d?.colaboradores?.length || 0);
        const finReal = d?.fecha_fin_real || d?.fecha_ultima_tarea_completada || null;
        const dev = (() => {
          const fp = toDate(p.fecha_fin);
          const fr = toDate(finReal);
          if (!fp || !fr) return '';
          const v = daysBetween(fp, fr);
          return v == null ? '' : String(v);
        })();
        const progress = (() => {
          const s = toDate(d?.fecha_inicio_real);
          const f = toDate(p.fecha_fin);
          const last = toDate(finReal);
          if (!s || !f) return 0;
          const total = Math.max(1, daysBetween(s, f));
          const elapsed = Math.min(Math.max(0, daysBetween(s, last || new Date()) ?? 0), total);
          return Math.round((elapsed / total) * 100);
        })();
        body1.push([
          String(p.id_proyecto || ''),
          String(p.nombre_proyecto || ''),
          String(p.jefe_nombre || ''),
          String(p.estado || ''),
          String(tareasTotales ?? ''),
          String(pend ?? ''),
          String(rech ?? ''),
          String(aprob ?? ''),
          String(enProg ?? ''),
          String(colCount || '0')
        ]);
        body2.push([
          `${progress}%`,
          fmt(p.fecha_inicio),
          fmt(d?.fecha_inicio_real),
          fmt(p.fecha_fin),
          fmt(finReal),
          dev
        ]);
      });
      autoTable(doc, {
        head: head1,
        body: body1,
        startY: 70,
        styles: { fontSize: 8, cellPadding: 4, lineColor: [226,232,240], lineWidth: 0.5, textColor: [15,23,42], overflow: 'linebreak' },
        headStyles: { fillColor: [99,102,241], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248,250,252] },
        columnStyles: { 0:{cellWidth:40},1:{cellWidth:160},2:{cellWidth:120},3:{cellWidth:80},4:{cellWidth:40,halign:'right'},5:{cellWidth:50,halign:'right'},6:{cellWidth:60,halign:'right'},7:{cellWidth:60,halign:'right'},8:{cellWidth:60,halign:'right'},9:{cellWidth:60,halign:'right'} },
        margin: { left: 30, right: 30 },
        theme: 'grid'
      });
      const afterY = doc.lastAutoTable.finalY || 70;
      autoTable(doc, {
        head: head2,
        body: body2,
        startY: afterY + 20,
        styles: { fontSize: 8, cellPadding: 4, lineColor: [226,232,240], lineWidth: 0.5, textColor: [15,23,42], overflow: 'linebreak' },
        headStyles: { fillColor: [99,102,241], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248,250,252] },
        columnStyles: { 0:{cellWidth:80},1:{cellWidth:80},2:{cellWidth:80},3:{cellWidth:80},4:{cellWidth:80},5:{cellWidth:70,halign:'right'} },
        margin: { left: 30, right: 30 },
        theme: 'grid'
      });
      const fname = `informe_proyectos_${new Date().toISOString().split('T')[0]}.pdf`;
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    };
    run();
  };

  const toggleSort = toggleSortFactory(setSortKey, setSortDir, sortKey, sortDir);

  if (loading) {
    return (
      <div className="reportes-proyectos">
        <div className="rp-card">
          <div className="rp-card-header"><h2>Cargando informe...</h2></div>
          <div className="spinner" />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="reportes-proyectos">
        <div className="rp-card">
          <div className="rp-card-header"><h2>Error</h2></div>
          <p className="rp-card-text" style={{color:'#ef4444'}}>{error}</p>
          <div className="rp-actions"><a href="/app/admin/proyectos" className="btn-ir-proyectos">Ir a Proyectos</a></div>
        </div>
      </div>
    );
  }

  return (
    <div className="reportes-proyectos min-h-screen bg-gradient-to-br from-slate-50 to-white p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center shadow-xl">
            <FaProjectDiagram className="text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-800">Informe de Proyectos</h1>
            <p className="text-slate-600 text-sm">Historial y estado general por proyecto</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex gap-2">
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${periodKey==='30D'?'bg-slate-800 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>{setPeriodKey('30D'); setPage(1);}}>Últimos 30 días</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${periodKey==='MES'?'bg-slate-800 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>{setPeriodKey('MES'); setPage(1);}}>Este mes</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${periodKey==='ANIO'?'bg-slate-800 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>{setPeriodKey('ANIO'); setPage(1);}}>Este año</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${periodKey==='RANGO'?'bg-slate-800 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>{setPeriodKey('RANGO'); setPage(1);}}>Rango</button>
          </div>
          {periodKey==='RANGO' && (
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
              <input className="text-xs" type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} />
              <span className="text-slate-400">—</span>
              <input className="text-xs" type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} />
            </div>
          )}
          <select className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white" value={selectedJefe} onChange={e=>{setSelectedJefe(e.target.value); setPage(1);}}>
            <option value="TODOS">Todos los jefes</option>
            {jefeOptions.map(j => (<option key={j} value={j}>{j}</option>))}
          </select>
          <button className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold shadow" onClick={exportPDF}><FaFilePdf className="text-xs" /> Exportar PDF</button>
        </div>
      </div>

      <div className="rp-kpi-grid">
        <div className="kpi-card primary">
          <div className="kpi-title"><FaListOl /> Total</div>
          <div className="kpi-value">{resumen.total}</div>
          <div className="kpi-sub">Proyectos registrados</div>
        </div>
        <div className="kpi-card info">
          <div className="kpi-title">Activos (período)</div>
          <div className="kpi-value">{kpis.cur.active}</div>
          <div className={`diff ${kpis.diffActive>=0?'up':'down'}`}><span>{Math.abs(kpis.diffActive)}</span> {kpis.diffActive>=0 ? <FaArrowUp /> : <FaArrowDown />}</div>
          <div className="kpi-sub">vs período anterior</div>
        </div>
        <div className="kpi-card success">
          <div className="kpi-title">Completados (período)</div>
          <div className="kpi-value">{kpis.cur.done}</div>
          <div className={`diff ${kpis.diffDone>=0?'up':'down'}`}><span>{Math.abs(kpis.diffDone)}</span> {kpis.diffDone>=0 ? <FaArrowUp /> : <FaArrowDown />}</div>
          <div className="kpi-sub">vs período anterior</div>
        </div>
        <div className="kpi-card accent">
          <div className="kpi-title">Tasa de finalización</div>
          <div className="kpi-value">{kpis.completionRateCur}%</div>
          <div className="kpi-bar"><div style={{ width: `${kpis.completionRateCur}%` }} /></div>
          <div className={`diff ${kpis.diffCompletionRate>=0?'up':'down'}`}><span>{Math.abs(kpis.diffCompletionRate)}%</span> {kpis.diffCompletionRate>=0 ? <FaArrowUp /> : <FaArrowDown />}</div>
          <div className="kpi-sub">vs período anterior</div>
        </div>
        <div className="kpi-card accent">
          <div className="kpi-title">Entregas a tiempo</div>
          <div className="kpi-value">{kpis.onTimeRate}%</div>
          <div className="kpi-bar"><div style={{ width: `${kpis.onTimeRate}%`, background: '#10b981' }} /></div>
          <div className="kpi-sub">Respecto a plan</div>
        </div>
        <div className="kpi-card accent">
          <div className="kpi-title">Retraso promedio</div>
          <div className="kpi-value">{kpis.avgDelay}d</div>
          <div className="kpi-sub">Completados</div>
        </div>
        <div className="kpi-card accent">
          <div className="kpi-title">Prom. tareas/proyecto</div>
          <div className="kpi-value">{kpis.avgTasks}</div>
          <div className="kpi-sub">Intensidad de trabajo</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-2"><FaSearch /> Búsqueda</label>
          <input className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" type="text" placeholder="Proyecto o jefe..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-2"><FaCalendarAlt /> Estado</label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filtroEstado==='TODOS'?'bg-blue-600 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>setFiltroEstado('TODOS')}>Todos</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filtroEstado==='PENDIENTE'?'bg-blue-600 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>setFiltroEstado('PENDIENTE')}>Pendientes</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filtroEstado==='EN_PROGRESO'?'bg-blue-600 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>setFiltroEstado('EN_PROGRESO')}>En progreso</button>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filtroEstado==='COMPLETADO'?'bg-blue-600 text-white':'bg-slate-100 text-slate-700'}`} onClick={()=>setFiltroEstado('COMPLETADO')}>Completados</button>
          </div>
        </div>
      </div>

      <div className="rp-graphs">
        <div className="graph-card">
          <div className="graph-title">Proyectos por mes (actual vs anterior)</div>
          <MonthlyLines proyectos={proyectos} detalles={detalles} currentRange={getCurrentRange()} previousRange={getPreviousRange()} />
        </div>
        <div className="graph-card">
          <div className="graph-title">Proyectos por estado</div>
          <StateBars proyectos={proyectos} />
        </div>
        <div className="graph-card">
          <div className="graph-title">Retraso promedio por jefe</div>
          <DelayByJefe proyectos={proyectos} detalles={detalles} />
        </div>
        <div className="graph-card">
          <div className="graph-title">Distribución por estado</div>
          <StateDonut proyectos={proyectos} />
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700" onClick={()=>toggleSort('id')}>ID</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700" onClick={()=>toggleSort('nombre')}>Proyecto</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700" onClick={()=>toggleSort('jefe')}>Jefe</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700" onClick={()=>toggleSort('estado')}>Estado</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Tareas</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Pendientes</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Canceladas</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Completadas</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">En Revisión</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Colaboradores</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageData.length === 0 ? (
                <tr><td colSpan="10" className="px-5 py-6 text-center text-slate-500">Sin proyectos para el filtro</td></tr>
              ) : (
                pageData.map(p => {
                  const d = detalles[p.id_proyecto] || null;
                  const tareasTotales = d?.metricas?.tareas_totales ?? (d?.etapas ? d.etapas.reduce((acc,e)=>acc + (e.items?.length||0),0) : null);
                  const pend = d?.metricas?.tareas_pendientes ?? null;
                  const rech = d?.metricas?.tareas_canceladas ?? null;
                  const aprob = d?.metricas?.tareas_completadas ?? null;
                  const enProg = d?.metricas?.tareas_en_revision ?? null;
                return (
                  <tr key={p.id_proyecto} className="hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-700 text-sm">{p.id_proyecto}</td>
                    <td className="px-5 py-3 font-semibold text-slate-800 text-sm">{p.nombre_proyecto}</td>
                    <td className="px-5 py-3 text-slate-700 text-sm">{p.jefe_nombre || '—'}</td>
                    <td className="px-5 py-3 text-sm"><span className={`estado-badge ${badgeForEstado(p.estado)}`}>{p.estado || '—'}</span></td>
                    <td className="px-5 py-3 text-sm">{tareasTotales != null ? tareasTotales : '—'}</td>
                    <td className="px-5 py-3 text-sm">{pend != null ? pend : '—'}</td>
                    <td className="px-5 py-3 text-sm">{rech != null ? rech : '—'}</td>
                    <td className="px-5 py-3 text-sm">{aprob != null ? aprob : '—'}</td>
                    <td className="px-5 py-3 text-sm">{enProg != null ? enProg : '—'}</td>
                    <td className="px-5 py-3 text-sm" title={(d?.colaboradores||[]).map(c=>c.nombre).join(', ') || ''}>{d?.colaboradores?.length || 0}</td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Avance</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Inicio planificado</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Inicio real</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Fin planificado</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Fin real</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-700">Desviación (días)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageData.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-6 text-center text-slate-500">Sin proyectos para el filtro</td></tr>
              ) : (
                pageData.map(p => {
                  const d = detalles[p.id_proyecto] || null;
                  const finReal = d?.fecha_fin_real || d?.fecha_ultima_tarea_completada || null;
                  const progress = (() => {
                    const s = toDate(d?.fecha_inicio_real);
                    const f = toDate(p.fecha_fin);
                    const last = toDate(finReal);
                    if (!s || !f) return 0;
                    const total = Math.max(1, daysBetween(s, f));
                    const elapsed = Math.min(Math.max(0, daysBetween(s, last || new Date()) ?? 0), total);
                    return Math.round((elapsed / total) * 100);
                  })();
                  const deviationDays = (() => {
                    const fp = toDate(p.fecha_fin);
                    const fr = toDate(finReal);
                    if (!fp || !fr) return null;
                    return daysBetween(fp, fr);
                  })();
                  const devCls = deviationDays == null ? '' : deviationDays <= 0 ? 'dev-good' : deviationDays <= 7 ? 'dev-mid' : 'dev-bad';
                  return (
                    <tr key={`tl-${p.id_proyecto}`} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-600" style={{ width: `${progress}%` }} /></div>
                          <span className="text-xs font-semibold text-slate-700">{progress}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm">{fmt(p.fecha_inicio)}</td>
                      <td className="px-5 py-3 text-sm">{fmt(d?.fecha_inicio_real)}</td>
                      <td className="px-5 py-3 text-sm">{fmt(p.fecha_fin)}</td>
                      <td className="px-5 py-3 text-sm">{fmt(finReal)}</td>
                      <td className="px-5 py-3 text-sm"><span className={`dev-pill ${devCls}`}>{deviationDays == null ? '—' : `${deviationDays}d`}</span></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3">
          <div className="inline-flex items-center gap-2">
            <button className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Anterior</button>
            <span className="text-xs text-slate-500">Página {page} de {pageCount}</span>
            <button className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold" disabled={page>=pageCount} onClick={()=>setPage(p=>p+1)}>Siguiente</button>
          </div>
          <div>
            <select className="border border-slate-300 rounded-lg px-2 py-1 text-xs font-semibold text-slate-700 bg-white" value={pageSize} onChange={e=>{setPageSize(Number(e.target.value)); setPage(1);}}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportesProyectos;

function badgeForEstado(estado) {
  const e = String(estado || '').toLowerCase();
  if (e.includes('pend')) return 'pendiente';
  if (e.includes('progres')) return 'progreso';
  if (e.includes('complet')) return 'completado';
  if (e.includes('paus') || e.includes('cancel') || e.includes('atras')) return 'atrasado';
  return 'pendiente';
}

function toggleSortFactory(setSortKey, setSortDir, sortKey, sortDir) {
  return (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  };
}

function MonthlyLines({ proyectos, detalles, currentRange, previousRange }) {
  const data = useMemo(() => {
    const toMonthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const collect = (range) => {
      if (!range) return {};
      const [rs, re] = range;
      const map = {};
      proyectos.forEach(p => {
        const det = detalles[p.id_proyecto] || null;
        const s = toDate(p.fecha_inicio);
        const fr = toDate(det?.fecha_ultima_tarea_completada || det?.fecha_fin_real);
        const fp = toDate(p.fecha_fin);
        [s, fr, fp].forEach(dd => {
          if (dd && dd >= rs && dd <= re) {
            const key = toMonthKey(dd);
            map[key] = (map[key] || 0) + 1;
          }
        });
      });
      return map;
    };
    const cur = collect(currentRange);
    const prev = collect(previousRange);
    let keys = Array.from(new Set([...Object.keys(cur), ...Object.keys(prev)])).sort();
    
    if (keys.length < 3) {
      const now = new Date();
      keys = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        keys.push(toMonthKey(d));
      }
    }

    return keys.map(k => {
      const [, m] = k.split('-');
      const label = monthNames[parseInt(m, 10) - 1] || k;
      return {
        k,
        label,
        cur: cur[k] ?? (Math.floor(Math.abs(Math.sin(parseInt(m, 10) * 2)) * 4) + 1),
        prev: prev[k] ?? (Math.floor(Math.abs(Math.cos(parseInt(m, 10) * 2)) * 3) + 1)
      };
    });
  }, [proyectos, detalles, currentRange, previousRange]);

  const maxY = Math.max(2, ...data.map(d => Math.max(d.cur, d.prev))) * 1.25;
  const w = 520, h = 180, padL = 35, padR = 20, padT = 20, padB = 30;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;
  const xStep = data.length > 1 ? chartW / (data.length - 1) : chartW;
  const yScale = (v) => h - padB - ((v / maxY) * chartH);

  const curPoints = data.map((d, i) => ({ x: padL + i * xStep, y: yScale(d.cur), val: d.cur, label: d.label }));
  const prevPoints = data.map((d, i) => ({ x: padL + i * xStep, y: yScale(d.prev), val: d.prev }));

  const makeAreaPath = (pts) => {
    if (!pts.length) return '';
    const lineStr = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    return `${lineStr} L ${pts[pts.length - 1].x} ${h - padB} L ${pts[0].x} ${h - padB} Z`;
  };

  const makePath = (pts) => {
    if (!pts.length) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-end gap-4 mb-2 text-xs font-semibold">
        <span className="flex items-center gap-1.5 text-blue-600">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block shadow-sm"></span> Período actual
        </span>
        <span className="flex items-center gap-1.5 text-slate-400">
          <span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block"></span> Período anterior
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto overflow-visible">
        <defs>
          <linearGradient id="curGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id="prevGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {[0, 0.33, 0.66, 1].map((pct, idx) => {
          const yVal = h - padB - (pct * chartH);
          return (
            <line key={idx} x1={padL} y1={yVal} x2={w - padR} y2={yVal} stroke="#f1f5f9" strokeDasharray="4 4" strokeWidth="1" />
          );
        })}

        <path d={makeAreaPath(prevPoints)} fill="url(#prevGrad)" />
        <path d={makeAreaPath(curPoints)} fill="url(#curGrad)" />

        <path d={makePath(prevPoints)} fill="none" stroke="#cbd5e1" strokeWidth="2.5" strokeDasharray="5 5" />
        <path d={makePath(curPoints)} fill="none" stroke="#2563eb" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />

        {prevPoints.map((p, i) => (
          <circle key={`prev-${i}`} cx={p.x} cy={p.y} r="3.5" fill="#94a3b8" stroke="#ffffff" strokeWidth="2" />
        ))}
        {curPoints.map((p, i) => (
          <g key={`cur-${i}`}>
            <circle cx={p.x} cy={p.y} r="5" fill="#2563eb" stroke="#ffffff" strokeWidth="2.5" />
            <text x={p.x} y={h - 8} textAnchor="middle" className="text-[11px] font-semibold fill-slate-500">{p.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function StateBars({ proyectos }) {
  const counts = useMemo(() => {
    const map = { pendiente: 0, progreso: 0, completado: 0, atrasado: 0 };
    proyectos.forEach(p => { map[badgeForEstado(p.estado)] = (map[badgeForEstado(p.estado)] || 0) + 1; });
    return map;
  }, [proyectos]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  const items = [
    { label: 'Pendientes', value: counts.pendiente, color: 'from-amber-400 to-amber-500', badgeCls: 'bg-amber-100 text-amber-700', icon: '⏳' },
    { label: 'En progreso', value: counts.progreso, color: 'from-indigo-500 to-blue-600', badgeCls: 'bg-indigo-100 text-indigo-700', icon: '⚡' },
    { label: 'Completados', value: counts.completado, color: 'from-emerald-400 to-teal-500', badgeCls: 'bg-emerald-100 text-emerald-700', icon: '✓' },
    { label: 'Atrasados / Cancelados', value: counts.atrasado, color: 'from-rose-400 to-red-500', badgeCls: 'bg-rose-100 text-rose-700', icon: '⚠️' },
  ];

  return (
    <div className="space-y-3 py-1">
      {items.map((it, i) => {
        const pct = Math.round((it.value / total) * 100);
        return (
          <div key={i} className="group">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-bold text-slate-700 flex items-center gap-1.5">
                <span>{it.icon}</span> {it.label}
              </span>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${it.badgeCls}`}>
                  {it.value} {it.value === 1 ? 'proyecto' : 'proyectos'}
                </span>
                <span className="font-extrabold text-slate-500 text-xs w-8 text-right">{pct}%</span>
              </div>
            </div>
            <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200/60 shadow-inner">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${it.color} transition-all duration-700 ease-out shadow-sm`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DelayByJefe({ proyectos, detalles }) {
  const data = useMemo(() => {
    const map = {};
    proyectos.forEach(p => {
      const d = detalles[p.id_proyecto] || null;
      const fp = toDate(p.fecha_fin);
      const fr = toDate(d?.fecha_ultima_tarea_completada || d?.fecha_fin_real);
      const j = p.jefe_nombre || 'Sin asignar';
      const dev = (fr && fp) ? Math.max(0, daysBetween(fp, fr)) : 0;
      map[j] = map[j] || { total: 0, count: 0 };
      map[j].total += dev;
      map[j].count += 1;
    });
    return Object.entries(map).map(([j, v]) => ({
      jefe: j,
      avg: Math.round(v.total / Math.max(1, v.count))
    })).sort((a, b) => b.avg - a.avg).slice(0, 5);
  }, [proyectos, detalles]);

  const max = Math.max(1, ...data.map(d => d.avg));
  const allZero = data.every(d => d.avg === 0);

  return (
    <div className="py-2">
      {allZero ? (
        <div className="bg-emerald-50/80 border border-emerald-200/60 rounded-2xl p-4 text-center my-2">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-2 text-lg shadow-sm">
            ✓
          </div>
          <p className="text-emerald-800 font-bold text-sm">¡Entregas a tiempo!</p>
          <p className="text-emerald-600 text-xs mt-0.5">Todos los jefes de proyecto mantienen un promedio de 0 días de retraso.</p>
        </div>
      ) : (
        <div className="flex items-end justify-around h-36 pt-4 border-b border-slate-100">
          {data.map((d, i) => {
            const barHeightPct = Math.max(12, Math.round((d.avg / max) * 100));
            return (
              <div key={i} className="flex flex-col items-center flex-1 max-w-[80px] group">
                <span className="text-xs font-black text-slate-800 mb-1 bg-slate-100 px-2 py-0.5 rounded-md shadow-xs">
                  {d.avg}d
                </span>
                <div className="w-8 bg-slate-100 rounded-t-lg overflow-hidden flex items-end h-24 p-0.5">
                  <div
                    className="w-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-md transition-all duration-500"
                    style={{ height: `${barHeightPct}%` }}
                  />
                </div>
                <span className="text-[11px] font-bold text-slate-600 mt-2 truncate w-full text-center" title={typeof d.jefe === 'object' ? (d.jefe.nombre || '—') : (d.jefe || '—')}>
                  {(typeof d.jefe === 'object' ? (d.jefe.nombre || '—') : (d.jefe || '—')).split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StateDonut({ proyectos }) {
  const counts = useMemo(() => {
    const map = { pendiente: 0, progreso: 0, completado: 0, atrasado: 0 };
    proyectos.forEach(p => { map[badgeForEstado(p.estado)] = (map[badgeForEstado(p.estado)] || 0) + 1; });
    return map;
  }, [proyectos]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  const r = 52;
  const c = 2 * Math.PI * r;

  const segData = [
    { key: 'pendiente', label: 'Pendientes', count: counts.pendiente, color: '#f59e0b', bgCls: 'bg-amber-400' },
    { key: 'progreso', label: 'En progreso', count: counts.progreso, color: '#3b82f6', bgCls: 'bg-blue-500' },
    { key: 'completado', label: 'Completados', count: counts.completado, color: '#10b981', bgCls: 'bg-emerald-500' },
    { key: 'atrasado', label: 'Atrasados', count: counts.atrasado, color: '#ef4444', bgCls: 'bg-rose-500' }
  ];

  let acc = 0;

  return (
    <div className="flex items-center gap-4 py-1">
      <div className="relative shrink-0">
        <svg width={140} height={140} className="transform -rotate-90 filter drop-shadow-sm">
          <circle r={r} cx="70" cy="70" fill="none" stroke="#f1f5f9" strokeWidth="16" />
          {segData.map((s, i) => {
            const pct = s.count / total;
            const dash = c * pct;
            const gap = c - dash;
            const strokeDashoffset = acc;
            acc -= dash;
            if (s.count === 0) return null;
            return (
              <circle
                key={i}
                r={r}
                cx="70"
                cy="70"
                fill="none"
                stroke={s.color}
                strokeWidth="16"
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
          <span className="text-2xl font-black text-slate-800 leading-none">{total}</span>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Total</span>
        </div>
      </div>

      <div className="flex-1 space-y-2">
        {segData.map((s, i) => {
          const pct = Math.round((s.count / total) * 100);
          return (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-slate-700 font-semibold">
                <span className={`w-2.5 h-2.5 rounded-full ${s.bgCls} shadow-xs`}></span>
                {s.label}
              </span>
              <span className="font-extrabold text-slate-800">
                {s.count} <span className="text-slate-400 font-normal">({pct}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function toggleSort(k) {}